import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { CircleCheck } from 'lucide-react'
import { PortalModal } from '@/components/portal/PortalModal'
import { PortalField } from '@/components/portal/PortalField'
import { PortalSelect } from '@/components/portal/PortalSelect'
import { CopyButton } from '@/components/portal/CopyButton'
import { useAccountOptions } from '@/hooks/useAccountOptions'
import { useAuthStore } from '@/store/auth'
import { clientApi } from '@/lib/api-client'
import { ensureClientProfile } from '@/lib/client-id'

const demoSchema = z.object({
  platform: z.enum(['standard', 'tradingview']),
  leverage: z.string(),
  initial_balance_usd: z.union([z.literal(1000), z.literal(5000), z.literal(20000)]),
})

type DemoForm = z.infer<typeof demoSchema>

const PLATFORMS: Array<{ value: DemoForm['platform']; label: string }> = [
  { value: 'standard', label: 'Standard' },
  { value: 'tradingview', label: 'TradingView' },
]

interface DemoAccountModalProps {
  open: boolean
  onClose: () => void
}

/* The form is replaced by the success view; keep keyboard focus inside the dialog. */
function focusOnMount(el: HTMLDivElement | null) {
  el?.focus()
}

export function DemoAccountModal({ open, onClose }: DemoAccountModalProps) {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { data: options } = useAccountOptions()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loginId, setLoginId] = useState<string | null>(null)

  const { register, handleSubmit, setValue, watch, formState: { isSubmitting } } = useForm<DemoForm>({
    resolver: zodResolver(demoSchema),
    defaultValues: { platform: 'standard', leverage: '1:100', initial_balance_usd: 1000 },
  })

  // A new opening shows the form again rather than the previous confirmation.
  useEffect(() => {
    if (!open) return
    setSuccess(null)
    setLoginId(null)
  }, [open])

  async function onSubmit(data: DemoForm) {
    setError(null)
    setSuccess(null)
    const clientId = await ensureClientProfile(user)
    if (!clientId) {
      setError(t('demo.clientRequired'))
      return
    }
    try {
      const result = await clientApi.createDemoAccount(clientId, {
        platform: data.platform,
        leverage: data.leverage,
        initial_balance_usd: data.initial_balance_usd,
      })
      setSuccess(t('demo.loginId', { id: result.login_id ?? '-' }))
      setLoginId(result.login_id != null ? String(result.login_id) : null)
      void queryClient.invalidateQueries({ queryKey: ['client', 'status'] })
      setTimeout(onClose, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'))
    }
  }

  return (
    <PortalModal
      open={open}
      onClose={onClose}
      title={t('demo.title')}
      subtitle={success ? undefined : t('demo.subtitle')}
      footer={
        success ? undefined : (
          <button type="submit" form="demo-account-form" className="prod-btn prod-btn--gold" disabled={isSubmitting}>
            {t('demo.create')}
          </button>
        )
      }
    >
      {success ? (
        <div ref={focusOnMount} className="prod-confirm prod-demo-done" role="status" tabIndex={-1}>
          <CircleCheck className="prod-confirm__icon" aria-hidden="true" />
          <div className="prod-confirm__body">
            <p className="prod-confirm__title">{t('demo.success')}</p>
            {loginId ? (
              <>
                <p className="prod-demo-done__label">{t('demo.loginIdLabel')}</p>
                <p className="prod-demo-done__id">{loginId}</p>
                <CopyButton value={loginId} label={`${t('demo.loginIdLabel')} ${loginId}`} />
                <p className="prod-confirm__text">{t('demo.successHint')}</p>
              </>
            ) : (
              <p className="prod-confirm__text">{success}</p>
            )}
          </div>
        </div>
      ) : (
        <form id="demo-account-form" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <fieldset className="prod-choice-field">
            <legend className="prod-field__label">{t('demo.platform')}</legend>
            <div className="prod-radio-group prod-radio-group--grid">
              {PLATFORMS.map((p) => (
                <label key={p.value} className="prod-radio prod-radio--card">
                  <input
                    type="radio"
                    name="demo-platform"
                    value={p.value}
                    checked={watch('platform') === p.value}
                    onChange={() => setValue('platform', p.value)}
                  />
                  <span className="prod-radio__body">
                    <span className="prod-radio__caption">Demo</span>
                    <span className="prod-radio__title">{p.label}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="prod-choice-field">
            <legend className="prod-field__label">{t('demo.initialBalance')}</legend>
            <div className="prod-radio-group prod-radio-group--grid">
              {(options?.demo_initial_balances ?? [1000, 5000, 20000]).map((bal) => (
                <label key={bal} className="prod-radio prod-radio--card">
                  <input
                    type="radio"
                    name="demo-initial-balance"
                    value={bal}
                    checked={watch('initial_balance_usd') === bal}
                    onChange={() => setValue('initial_balance_usd', Number(bal) as DemoForm['initial_balance_usd'])}
                  />
                  <span className="prod-radio__body">
                    <span className="prod-radio__caption">USD</span>
                    <span className="prod-radio__title prod-radio__title--num">{bal.toLocaleString()}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <PortalField label={t('demo.leverage')}>
            <PortalSelect {...register('leverage')}>
              <option value="">{t('demo.selectLeverage')}</option>
              {(options?.leverage_options ?? ['1:100']).map((lev) => (
                <option key={lev} value={lev}>
                  {lev}
                </option>
              ))}
            </PortalSelect>
          </PortalField>

          {error ? (
            <p className="prod-error" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      )}
    </PortalModal>
  )
}
